/**
 * Global Search Trigger
 *
 * A compact search trigger button for the TopBar.
 * Desktop: Shows input-like button with placeholder and ⌘K hint
 * Mobile: Shows search icon only
 */

import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useGlobalSearch } from './useGlobalSearch';
import { cn } from '@/lib/utils';

interface GlobalSearchTriggerProps {
  className?: string;
}

/**
 * Desktop search trigger - looks like an input but opens command palette
 */
export function GlobalSearchTrigger({ className }: GlobalSearchTriggerProps) {
  const { t } = useTranslation('common');
  const { open } = useGlobalSearch();

  return (
    <button
      type="button"
      onClick={open}
      className={cn(
        'hidden sm:inline-flex items-center gap-2',
        'h-9 w-64 px-3',
        'rounded-xl border border-border bg-background',
        'text-sm text-muted-foreground',
        'hover:bg-muted hover:text-foreground',
        'transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        className
      )}
      aria-label={t('globalSearch.open', 'Open zoeken')}
    >
      <Search className="h-4 w-4 shrink-0" />
      <span className="flex-1 text-left truncate">
        {t('globalSearch.placeholder', 'Zoeken...')}
      </span>
      <kbd className="pointer-events-none hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
        <span className="text-xs">⌘</span>K
      </kbd>
    </button>
  );
}

/**
 * Mobile search trigger - icon button only
 */
export function GlobalSearchTriggerMobile({ className }: GlobalSearchTriggerProps) {
  const { t } = useTranslation('common');
  const { open } = useGlobalSearch();

  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={open}
      className={cn('h-11 w-11 shrink-0 sm:hidden', className)}
      aria-label={t('globalSearch.open', 'Open zoeken')}
    >
      <Search className="h-5 w-5" />
    </Button>
  );
}
